/**
 * 수입관리 (수입원천·수입결의·수납결의·조정·과오납·불납결손)
 * - API(isBdApiEnabled): BdIncome 전용 테이블 · 아니면 localStorage
 * - 키: erp.income.{source|resolution|receipt|adjust|overpay|writeoff}|{tenant}|{fy}
 */

import { isBdApiEnabled } from 'config/bdApiConfig';
import { cacheSet } from 'services/commonQuery';
import {
  apiLoadSources,
  apiSaveSources,
  apiLoadResolutionBundle,
  apiSaveResolutionBundle,
  apiLoadReceipts,
  apiSaveReceipts,
  apiLoadAdjusts,
  apiSaveAdjusts,
  apiLoadOverpays,
  apiSaveOverpays,
  apiLoadWriteoffs,
  apiSaveWriteoffs,
} from 'income/incomeApi';

export const SOURCE_STORAGE_PREFIX = 'erp.income.source';
export const RESOLUTION_STORAGE_PREFIX = 'erp.income.resolution';
export const RECEIPT_STORAGE_PREFIX = 'erp.income.receipt';
export const ADJUST_STORAGE_PREFIX = 'erp.income.adjust';
export const OVERPAY_STORAGE_PREFIX = 'erp.income.overpay';
export const WRITEOFF_STORAGE_PREFIX = 'erp.income.writeoff';

/** 공통 상태 */
export const STATUS = {
  DRAFT: 'DRAFT',
  REQUESTED: 'REQUESTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
};

export const STATUS_OPTIONS = [
  { value: STATUS.DRAFT, label: '작성중' },
  { value: STATUS.REQUESTED, label: '승인요청' },
  { value: STATUS.APPROVED, label: '승인' },
  { value: STATUS.REJECTED, label: '반려' },
];

/** 회계처리 모드 (기관 환경설정 income.accountingMode) */
export const ACCOUNTING_MODE = {
  MATCHED: 'MATCHED',
  SEPARATED: 'SEPARATED',
};

export const INCOME_ACCOUNTING_MODE_OPTIONS = [
  { value: ACCOUNTING_MODE.MATCHED, label: '일치형 승인시 전표' },
  { value: ACCOUNTING_MODE.SEPARATED, label: '분리형 분개검증 후 전표' },
];

/** 수입결의 승인 시 수납결의 자동생성 (기관 환경설정 income.receiptOnIncomeApprove) */
export const RECEIPT_ON_INCOME_APPROVE_OPTIONS = [
  { value: 'N', label: 'N (수납결의 별도)' },
  { value: 'Y', label: 'Y (승인 시 수납결의 자동·현금주의)' },
];

/** 수입원천 유형 */
export const SOURCE_TYPE = {
  LEASE: 'LEASE',
  SUBSIDY: 'SUBSIDY',
  FEE: 'FEE',
};

export const SOURCE_TYPE_OPTIONS = [
  { value: SOURCE_TYPE.LEASE, label: '임대' },
  { value: SOURCE_TYPE.SUBSIDY, label: '보조금' },
  { value: SOURCE_TYPE.FEE, label: '수수료' },
];

export const FEE_KIND_OPTIONS = [
  { value: '상수도', label: '상수도' },
  { value: '하수도', label: '하수도' },
  { value: '기타', label: '기타' },
];

export const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

export function todayYmd(d = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function createUid(prefix = 'INC') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createSourceUid() {
  return createUid('SRC');
}

export function createResolutionUid() {
  return createUid('IRES');
}

export function createResolutionDetailUid() {
  return createUid('IRESD');
}

export function createReceiptUid() {
  return createUid('RCP');
}

export function createAdjustUid() {
  return createUid('ADJ');
}

export function createOverpayUid() {
  return createUid('OVP');
}

export function createWriteoffUid() {
  return createUid('WOF');
}

function labelOf(options, code) {
  return options.find((o) => o.value === code)?.label || code || '';
}

export function statusLabel(code) {
  return labelOf(STATUS_OPTIONS, code);
}

export function sourceTypeLabel(code) {
  return labelOf(SOURCE_TYPE_OPTIONS, code);
}

function storageKey(prefix, dims = {}) {
  return [prefix, String(dims.tenantCode || '').trim(), String(dims.fiscalYear || '').trim()].join('|');
}

function readRows(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch {
    return [];
  }
}

function writeRows(key, dims, rows) {
  localStorage.setItem(
    key,
    JSON.stringify({
      dims: { tenantCode: dims.tenantCode, fiscalYear: dims.fiscalYear },
      rows: rows || [],
      savedAt: new Date().toISOString(),
    })
  );
  return rows || [];
}

function readBundle(key, detailKeys = ['details']) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      const empty = { masters: [] };
      detailKeys.forEach((k) => {
        empty[k] = [];
      });
      return empty;
    }
    const data = JSON.parse(raw);
    const out = { masters: Array.isArray(data?.masters) ? data.masters : [] };
    detailKeys.forEach((k) => {
      out[k] = Array.isArray(data?.[k]) ? data[k] : [];
    });
    return out;
  } catch {
    const empty = { masters: [] };
    detailKeys.forEach((k) => {
      empty[k] = [];
    });
    return empty;
  }
}

function writeBundle(key, payload) {
  localStorage.setItem(
    key,
    JSON.stringify({
      ...payload,
      savedAt: new Date().toISOString(),
    })
  );
}

function dimsKey(dims = {}) {
  return `${String(dims.tenantCode || '').trim()}|${String(dims.fiscalYear || '').trim()}`;
}

const sourceCache = new Map();
const resolutionCache = new Map();
const receiptCache = new Map();
const adjustCache = new Map();
const overpayCache = new Map();
const writeoffCache = new Map();

function keepRows(rows) {
  return (rows || []).filter((r) => r && r.mod !== 'D').map((r) => ({ ...r, mod: '' }));
}

async function persistList({ dims, cache, rows, apiSave, ensureUid, localWrite }) {
  const key = dimsKey(dims);
  const kept = keepRows(rows);
  cacheSet(cache, key, kept);
  if (isBdApiEnabled()) {
    return apiSave(dims, kept, ensureUid);
  }
  localWrite(kept);
  return kept;
}

async function fetchList({ dims, cache, apiLoad, localRead }) {
  const key = dimsKey(dims);
  if (isBdApiEnabled()) {
    return apiLoad(dims);
  }
  const rows = localRead();
  cacheSet(cache, key, rows);
  return rows;
}

function nextSeqNo(masters = [], field = 'resolutionNo') {
  const nums = (masters || [])
    .map((m) => Number(String(m[field] || '').replace(/\D/g, '')))
    .filter((n) => Number.isFinite(n) && n > 0);
  const max = nums.length ? Math.max(...nums) : 0;
  return String(max + 1).padStart(4, '0');
}

// ─── Source (수입원천) ───────────────────────────────────────────

export function emptySourceRow(partial = {}) {
  return {
    sourceUid: '',
    sourceNo: '',
    sourceType: SOURCE_TYPE.LEASE,
    vendorCode: '',
    vendorName: '',
    contractAmt: 0,
    invoiceDay: 1,
    projectName: '',
    subjectCode: '',
    subjectName: '',
    amount: 0,
    feeKind: '기타',
    remark: '',
    useYn: 'Y',
    mod: '',
    ...partial,
  };
}

export async function loadSources(dims) {
  return fetchList({
    dims,
    cache: sourceCache,
    apiLoad: apiLoadSources,
    localRead: () => readRows(storageKey(SOURCE_STORAGE_PREFIX, dims)),
  });
}

export async function saveSources(dims, rows) {
  return persistList({
    dims,
    cache: sourceCache,
    rows,
    apiSave: apiSaveSources,
    ensureUid: () => createSourceUid(),
    localWrite: (kept) => writeRows(storageKey(SOURCE_STORAGE_PREFIX, dims), dims, kept),
  });
}

export function nextSourceNo(rows = []) {
  return nextSeqNo(rows, 'sourceNo');
}

export function filterSources(rows = [], filter = {}) {
  let list = (rows || []).filter((r) => r.mod !== 'D');
  const st = String(filter.sourceType || '').trim();
  if (st) list = list.filter((r) => r.sourceType === st);
  const useYn = String(filter.useYn || '').trim();
  if (useYn) list = list.filter((r) => r.useYn === useYn);
  return list;
}

// ─── Income Resolution (수입결의) ────────────────────────────────

export function emptyResolutionMaster(partial = {}) {
  return {
    resolutionUid: '',
    resolutionNo: '',
    fiscalYear: '',
    sourceUid: '',
    sourceNo: '',
    deptCode: '',
    deptName: '',
    projectCode: '',
    projectName: '',
    subjectCode: '',
    subjectName: '',
    fundCode: '',
    fundName: '',
    balanceUid: '',
    balanceAmt: 0,
    resolutionAmt: 0,
    resolutionDate: '',
    remark: '',
    status: STATUS.DRAFT,
    registerDate: '',
    approveDate: '',
    slipYn: 'N',
    receiptLinkedYn: 'N',
    requestAt: '',
    rejectReason: '',
    mod: '',
    ...partial,
  };
}

export function emptyResolutionVendorRow(partial = {}) {
  return {
    detailUid: '',
    resolutionUid: '',
    vendorCode: '',
    vendorName: '',
    amount: 0,
    remark: '',
    mod: '',
    ...partial,
  };
}

export async function loadResolutionBundle(dims) {
  if (isBdApiEnabled()) {
    return apiLoadResolutionBundle(dims);
  }
  const bundle = readBundle(storageKey(RESOLUTION_STORAGE_PREFIX, dims), ['vendors']);
  cacheSet(resolutionCache, dimsKey(dims), bundle);
  return bundle;
}

export async function saveResolutionBundle(dims, masters, vendors) {
  const nextM = keepRows(masters);
  const nextV = keepRows(vendors);
  const bundle = { masters: nextM, vendors: nextV };
  cacheSet(resolutionCache, dimsKey(dims), bundle);
  if (isBdApiEnabled()) {
    return apiSaveResolutionBundle(
      dims,
      nextM,
      nextV,
      () => createResolutionUid(),
      () => createResolutionDetailUid()
    );
  }
  writeBundle(storageKey(RESOLUTION_STORAGE_PREFIX, dims), bundle);
  return bundle;
}

export function nextResolutionNo(masters = []) {
  return nextSeqNo(masters, 'resolutionNo');
}

export function filterResolutionsForRegister(masters = [], filter = {}) {
  let list = [...(masters || [])];
  const st = String(filter.status || '').trim();
  if (st) list = list.filter((m) => m.status === st);
  return list.filter((m) => m.mod !== 'D');
}

export function filterResolutionsForApprove(masters = [], filter = {}) {
  let list = (masters || []).filter((m) => m.mod !== 'D');
  const st = String(filter.status || '').trim();
  if (st) {
    list = list.filter((m) => m.status === st);
  } else {
    list = list.filter((m) => m.status === STATUS.REQUESTED);
  }
  return list;
}

/** 승인 화면용 — 마스터+거래처 디테일 평탄화 */
export async function loadJoinedForApprove(dims) {
  const bundle = await loadResolutionBundle(dims);
  const rows = [];
  (bundle.masters || [])
    .filter((m) => m.mod !== 'D' && m.status === STATUS.REQUESTED)
    .forEach((m) => {
      const dets = (bundle.vendors || []).filter(
        (d) => d.resolutionUid === m.resolutionUid && d.mod !== 'D'
      );
      if (!dets.length) {
        rows.push({
          ...m,
          statusNm: statusLabel(m.status),
        });
        return;
      }
      dets.forEach((d) => {
        rows.push({
          ...m,
          ...d,
          statusNm: statusLabel(m.status),
        });
      });
    });
  return rows;
}

export function requestResolutionApproval(masters, vendors, resolutionUid) {
  const uid = String(resolutionUid || '').trim();
  const nextM = (masters || []).map((m) => {
    if (m.resolutionUid !== uid) return m;
    return {
      ...m,
      status: STATUS.REQUESTED,
      requestAt: new Date().toISOString(),
    };
  });
  return { masters: nextM, vendors: vendors || [] };
}

/**
 * 수입결의 승인
 * - slipYn='Y' 전표 stub
 * - receiptOnApprove==='Y' 이면 승인된 수납결의 stub 자동생성 (현금주의)
 */
export async function approveResolution(masters, vendors, resolutionUid, options = {}) {
  const uid = String(resolutionUid || '').trim();
  const today = todayYmd();
  const receiptOnApprove = options.receiptOnApprove === 'Y';

  if (isBdApiEnabled()) {
    const { default: api } = await import('services/api');
    const res = await api.post(`/bd/income-resolution/${encodeURIComponent(uid)}/approve`, {
      receiptOnApprove: receiptOnApprove ? 'Y' : 'N',
    });
    const data = res.data || {};
    if (data.success === false) {
      throw new Error(data.Msg || data.message || '승인에 실패했습니다.');
    }
    const nextM = (masters || []).map((m) => {
      if (m.resolutionUid !== uid) return m;
      return {
        ...m,
        status: STATUS.APPROVED,
        approveDate: today,
        slipYn: 'Y',
        receiptLinkedYn: receiptOnApprove ? 'Y' : m.receiptLinkedYn || 'N',
      };
    });
    return {
      masters: nextM,
      vendors: vendors || [],
      receiptBundle: data.receipt ? { stub: data.receipt } : null,
    };
  }

  const nextM = (masters || []).map((m) => {
    if (m.resolutionUid !== uid) return m;
    return {
      ...m,
      status: STATUS.APPROVED,
      approveDate: today,
      slipYn: 'Y',
      receiptLinkedYn: receiptOnApprove ? 'Y' : m.receiptLinkedYn || 'N',
    };
  });

  let receiptBundle = null;
  if (receiptOnApprove) {
    const master = nextM.find((m) => m.resolutionUid === uid);
    if (master) {
      receiptBundle = await createApprovedReceiptStub(options.dims, master);
    }
  }

  return { masters: nextM, vendors: vendors || [], receiptBundle };
}

export function rejectResolution(masters, vendors, resolutionUid, reason = '반려') {
  const uid = String(resolutionUid || '').trim();
  const nextM = (masters || []).map((m) => {
    if (m.resolutionUid !== uid) return m;
    return {
      ...m,
      status: STATUS.REJECTED,
      rejectReason: reason,
    };
  });
  return { masters: nextM, vendors: vendors || [] };
}

/** 승인 시 자동생성되는 수납결의 stub (현금주의) */
async function createApprovedReceiptStub(dims, resolutionMaster) {
  if (!dims?.tenantCode || !dims?.fiscalYear) return null;
  const receiptBundle = await loadReceiptBundle(dims);
  const receiptUid = createReceiptUid();
  const receiptNo = nextReceiptNo(receiptBundle.masters);
  const today = todayYmd();

  const stub = emptyReceiptMaster({
    receiptUid,
    receiptNo,
    incomeResolutionUid: resolutionMaster.resolutionUid,
    incomeResolutionNo: resolutionMaster.resolutionNo,
    fiscalYear: dims.fiscalYear,
    deptCode: resolutionMaster.deptCode,
    deptName: resolutionMaster.deptName,
    projectCode: resolutionMaster.projectCode,
    projectName: resolutionMaster.projectName,
    subjectCode: resolutionMaster.subjectCode,
    subjectName: resolutionMaster.subjectName,
    fundCode: resolutionMaster.fundCode,
    fundName: resolutionMaster.fundName,
    receiptAmt: resolutionMaster.resolutionAmt || 0,
    receiptDate: today,
    bankName: '',
    accountNo: '',
    remark: `수입결의 ${resolutionMaster.resolutionNo || ''} 승인 자동생성 (현금주의)`,
    status: STATUS.APPROVED,
    approveDate: today,
    slipYn: 'Y',
    registerDate: today,
    mod: 'A',
  });

  const masters = [...(receiptBundle.masters || []), stub];
  await saveReceiptBundle(dims, masters);
  return { masters, stub };
}

// ─── Receipt Resolution (수납결의) ───────────────────────────────

export function emptyReceiptMaster(partial = {}) {
  return {
    receiptUid: '',
    receiptNo: '',
    incomeResolutionUid: '',
    incomeResolutionNo: '',
    fiscalYear: '',
    deptCode: '',
    deptName: '',
    projectCode: '',
    projectName: '',
    subjectCode: '',
    subjectName: '',
    fundCode: '',
    fundName: '',
    receiptAmt: 0,
    receiptDate: '',
    bankName: '',
    accountNo: '',
    remark: '',
    status: STATUS.DRAFT,
    registerDate: '',
    approveDate: '',
    slipYn: 'N',
    requestAt: '',
    rejectReason: '',
    mod: '',
    ...partial,
  };
}

export async function loadReceiptBundle(dims) {
  if (isBdApiEnabled()) {
    return apiLoadReceipts(dims);
  }
  const b = readBundle(storageKey(RECEIPT_STORAGE_PREFIX, dims), []);
  const masters = b.masters || [];
  cacheSet(receiptCache, dimsKey(dims), masters);
  return { masters };
}

export async function saveReceiptBundle(dims, masters) {
  if (isBdApiEnabled()) {
    return apiSaveReceipts(dims, keepRows(masters), () => createReceiptUid());
  }
  const kept = keepRows(masters);
  cacheSet(receiptCache, dimsKey(dims), kept);
  writeBundle(storageKey(RECEIPT_STORAGE_PREFIX, dims), { masters: kept });
  return { masters: kept };
}

export function nextReceiptNo(masters = []) {
  return nextSeqNo(masters, 'receiptNo');
}

export function filterReceiptsForRegister(masters = [], filter = {}) {
  let list = [...(masters || [])];
  const st = String(filter.status || '').trim();
  if (st) list = list.filter((m) => m.status === st);
  return list.filter((m) => m.mod !== 'D');
}

export function filterReceiptsForApprove(masters = [], filter = {}) {
  let list = (masters || []).filter((m) => m.mod !== 'D');
  const st = String(filter.status || '').trim();
  if (st) {
    list = list.filter((m) => m.status === st);
  } else {
    list = list.filter((m) => m.status === STATUS.REQUESTED);
  }
  return list;
}

/** 승인된 수입결의 중 미수납·부분수납 후보 */
export async function loadReceiptCandidates(dims) {
  const resBundle = await loadResolutionBundle(dims);
  const receiptBundle = await loadReceiptBundle(dims);

  const receiptSumByRes = new Map();
  (receiptBundle.masters || [])
    .filter((r) => r.mod !== 'D' && r.status === STATUS.APPROVED)
    .forEach((r) => {
      const key = r.incomeResolutionUid;
      receiptSumByRes.set(key, (receiptSumByRes.get(key) || 0) + Number(r.receiptAmt || 0));
    });

  return (resBundle.masters || [])
    .filter((m) => m.mod !== 'D' && m.status === STATUS.APPROVED)
    .filter((m) => {
      const sum = receiptSumByRes.get(m.resolutionUid) || 0;
      return sum < Number(m.resolutionAmt || 0);
    })
    .map((m) => ({
      value: m.resolutionUid,
      label: `${m.resolutionNo} · ${m.deptName || ''} · ${Number(m.resolutionAmt || 0).toLocaleString()}원`,
      ...m,
      receiptedAmt: receiptSumByRes.get(m.resolutionUid) || 0,
      remainAmt: Number(m.resolutionAmt || 0) - (receiptSumByRes.get(m.resolutionUid) || 0),
    }));
}

export function requestReceiptApproval(masters, receiptUid) {
  const uid = String(receiptUid || '').trim();
  const nextM = (masters || []).map((m) => {
    if (m.receiptUid !== uid) return m;
    return {
      ...m,
      status: STATUS.REQUESTED,
      requestAt: new Date().toISOString(),
    };
  });
  return nextM;
}

export function approveReceipt(masters, receiptUid) {
  const uid = String(receiptUid || '').trim();
  const today = todayYmd();
  return (masters || []).map((m) => {
    if (m.receiptUid !== uid) return m;
    return {
      ...m,
      status: STATUS.APPROVED,
      approveDate: today,
      slipYn: 'Y',
    };
  });
}

export function rejectReceipt(masters, receiptUid, reason = '반려') {
  const uid = String(receiptUid || '').trim();
  return (masters || []).map((m) => {
    if (m.receiptUid !== uid) return m;
    return {
      ...m,
      status: STATUS.REJECTED,
      rejectReason: reason,
    };
  });
}

// ─── Adjust (조정결의) ───────────────────────────────────────────

export function emptyAdjustRow(partial = {}) {
  return {
    adjustUid: '',
    incomeResolutionUid: '',
    incomeResolutionNo: '',
    adjustDate: '',
    reason: '',
    adjustAmt: 0,
    registerDate: '',
    status: STATUS.DRAFT,
    approveDate: '',
    slipYn: 'N',
    requestAt: '',
    mod: '',
    ...partial,
  };
}

export async function loadAdjusts(dims) {
  return fetchList({
    dims,
    cache: adjustCache,
    apiLoad: apiLoadAdjusts,
    localRead: () => readRows(storageKey(ADJUST_STORAGE_PREFIX, dims)),
  });
}

export async function saveAdjusts(dims, rows) {
  return persistList({
    dims,
    cache: adjustCache,
    rows,
    apiSave: apiSaveAdjusts,
    ensureUid: () => createAdjustUid(),
    localWrite: (kept) => writeRows(storageKey(ADJUST_STORAGE_PREFIX, dims), dims, kept),
  });
}

export function filterAdjustsByResolution(rows = [], resolutionUid) {
  const uid = String(resolutionUid || '').trim();
  if (!uid) return [];
  return (rows || []).filter((r) => r.incomeResolutionUid === uid && r.mod !== 'D');
}

export function requestAdjustApproval(rows, adjustUid) {
  const uid = String(adjustUid || '').trim();
  return (rows || []).map((r) => {
    if (r.adjustUid !== uid) return r;
    return {
      ...r,
      status: STATUS.REQUESTED,
      requestAt: new Date().toISOString(),
    };
  });
}

export function approveAdjust(rows, adjustUid) {
  const uid = String(adjustUid || '').trim();
  const today = todayYmd();
  return (rows || []).map((r) => {
    if (r.adjustUid !== uid) return r;
    return {
      ...r,
      status: STATUS.APPROVED,
      approveDate: today,
      slipYn: 'Y',
    };
  });
}

export function rejectAdjust(rows, adjustUid, reason = '반려') {
  const uid = String(adjustUid || '').trim();
  return (rows || []).map((r) => {
    if (r.adjustUid !== uid) return r;
    return {
      ...r,
      status: STATUS.REJECTED,
      rejectReason: reason,
    };
  });
}

// ─── Overpayment (과오납) ────────────────────────────────────────

export function emptyOverpayRow(partial = {}) {
  return {
    overpayUid: '',
    receiptUid: '',
    receiptNo: '',
    incomeResolutionUid: '',
    incomeResolutionNo: '',
    overAmt: 0,
    processDate: '',
    reason: '',
    refundYn: 'N',
    status: STATUS.DRAFT,
    slipYn: 'N',
    requestAt: '',
    mod: '',
    ...partial,
  };
}

export async function loadOverpays(dims) {
  return fetchList({
    dims,
    cache: overpayCache,
    apiLoad: apiLoadOverpays,
    localRead: () => readRows(storageKey(OVERPAY_STORAGE_PREFIX, dims)),
  });
}

export async function saveOverpays(dims, rows) {
  return persistList({
    dims,
    cache: overpayCache,
    rows,
    apiSave: apiSaveOverpays,
    ensureUid: () => createOverpayUid(),
    localWrite: (kept) => writeRows(storageKey(OVERPAY_STORAGE_PREFIX, dims), dims, kept),
  });
}

/** 수납금액 > 결의금액 후보 + 기존 과오납 */
export async function loadOverpayCandidates(dims) {
  const resBundle = await loadResolutionBundle(dims);
  const receiptBundle = await loadReceiptBundle(dims);
  const existing = await loadOverpays(dims);
  const existingReceiptUids = new Set(
    (existing || []).filter((o) => o.mod !== 'D').map((o) => o.receiptUid)
  );

  const resMap = new Map(
    (resBundle.masters || []).map((m) => [m.resolutionUid, m])
  );

  const candidates = [];
  (receiptBundle.masters || [])
    .filter((r) => r.mod !== 'D' && r.status === STATUS.APPROVED)
    .forEach((r) => {
      const res = resMap.get(r.incomeResolutionUid);
      if (!res) return;
      const overAmt = Number(r.receiptAmt || 0) - Number(res.resolutionAmt || 0);
      if (overAmt <= 0) return;
      if (existingReceiptUids.has(r.receiptUid)) return;
      candidates.push(
        emptyOverpayRow({
          overpayUid: createOverpayUid(),
          receiptUid: r.receiptUid,
          receiptNo: r.receiptNo,
          incomeResolutionUid: r.incomeResolutionUid,
          incomeResolutionNo: r.incomeResolutionNo,
          overAmt,
          processDate: todayYmd(),
          reason: '수납 초과',
          mod: 'A',
        })
      );
    });

  return candidates;
}

export function requestOverpayApproval(rows, overpayUid) {
  const uid = String(overpayUid || '').trim();
  return (rows || []).map((r) => {
    if (r.overpayUid !== uid) return r;
    return {
      ...r,
      status: STATUS.REQUESTED,
      requestAt: new Date().toISOString(),
    };
  });
}

export function approveOverpay(rows, overpayUid) {
  const uid = String(overpayUid || '').trim();
  return (rows || []).map((r) => {
    if (r.overpayUid !== uid) return r;
    return {
      ...r,
      status: STATUS.APPROVED,
      slipYn: 'Y',
    };
  });
}

// ─── Write-off (불납결손) ───────────────────────────────────────

export function emptyWriteoffRow(partial = {}) {
  return {
    writeoffUid: '',
    incomeResolutionUid: '',
    incomeResolutionNo: '',
    writeoffAmt: 0,
    writeoffDate: '',
    legalBasis: '',
    reason: '',
    status: STATUS.DRAFT,
    slipYn: 'N',
    requestAt: '',
    mod: '',
    ...partial,
  };
}

export async function loadWriteoffs(dims) {
  return fetchList({
    dims,
    cache: writeoffCache,
    apiLoad: apiLoadWriteoffs,
    localRead: () => readRows(storageKey(WRITEOFF_STORAGE_PREFIX, dims)),
  });
}

export async function saveWriteoffs(dims, rows) {
  return persistList({
    dims,
    cache: writeoffCache,
    rows,
    apiSave: apiSaveWriteoffs,
    ensureUid: () => createWriteoffUid(),
    localWrite: (kept) => writeRows(storageKey(WRITEOFF_STORAGE_PREFIX, dims), dims, kept),
  });
}

export function requestWriteoffApproval(rows, writeoffUid) {
  const uid = String(writeoffUid || '').trim();
  return (rows || []).map((r) => {
    if (r.writeoffUid !== uid) return r;
    return {
      ...r,
      status: STATUS.REQUESTED,
      requestAt: new Date().toISOString(),
    };
  });
}

export function approveWriteoff(rows, writeoffUid) {
  const uid = String(writeoffUid || '').trim();
  const today = todayYmd();
  return (rows || []).map((r) => {
    if (r.writeoffUid !== uid) return r;
    return {
      ...r,
      status: STATUS.APPROVED,
      approveDate: today,
      slipYn: 'Y',
    };
  });
}
